<template>
  <div class="op-dashboard">

    <!-- ── Barra superior: última actualización + botón refresh ── -->
    <div class="op-topbar">
      <div class="op-topbar-left">
        <span class="op-last-update" v-if="lastUpdate">
          <i class="pi pi-clock"></i> Actualizado {{ lastUpdateLabel }}
        </span>
      </div>
      <div class="op-topbar-right">
        <label class="op-autorefresh-toggle" title="Auto-actualizar cada 5 min">
          <span class="op-toggle-label">Auto-refresh</span>
          <input type="checkbox" v-model="autoRefresh" @change="toggleAutoRefresh" />
          <span class="op-toggle-slider"></span>
        </label>
        <button class="op-refresh-btn" :class="{ 'op-refresh-btn--loading': loader }" @click="cargarDatos">
          <i class="pi pi-refresh"></i> Actualizar
        </button>
      </div>
    </div>

    <!-- ── Skeleton ── -->
    <template v-if="loader && !lastUpdate">
      <div class="op-skeleton-grid">
        <div v-for="n in 4" :key="n" class="op-skeleton-kpi"></div>
      </div>
      <div v-for="n in 3" :key="'s'+n" class="op-skeleton-charts" style="margin-bottom:1rem">
        <div class="op-skeleton-chart"></div>
        <div class="op-skeleton-chart"></div>
      </div>
    </template>

    <template v-else>
      <!-- ── KPI ROW ── -->
      <div class="op-kpi-grid">
        <div class="op-kpi-card">
          <div class="op-kpi-top">
            <div class="op-kpi-icon op-icon-blue"><i class="pi pi-truck"></i></div>
            <div v-if="kpi.vehiculos_delta !== null" :class="['op-kpi-badge', kpi.vehiculos_delta >= 0 ? 'op-badge-up' : 'op-badge-down']">
              <i :class="kpi.vehiculos_delta >= 0 ? 'pi pi-arrow-up' : 'pi pi-arrow-down'"></i> {{ Math.abs(kpi.vehiculos_delta) }}%
            </div>
          </div>
          <div class="op-kpi-label">VEHÍCULOS ACTIVOS</div>
          <div class="op-kpi-value">{{ kpi.vehiculos_activos }}</div>
          <div class="op-kpi-sub">{{ kpi.vehiculos_mes_anterior }} el mes anterior</div>
        </div>
        <div class="op-kpi-card">
          <div class="op-kpi-top">
            <div class="op-kpi-icon op-icon-green"><i class="pi pi-id-card"></i></div>
            <div v-if="kpi.conductores_delta !== null" :class="['op-kpi-badge', kpi.conductores_delta >= 0 ? 'op-badge-up' : 'op-badge-down']">
              <i :class="kpi.conductores_delta >= 0 ? 'pi pi-arrow-up' : 'pi pi-arrow-down'"></i> {{ Math.abs(kpi.conductores_delta) }}%
            </div>
          </div>
          <div class="op-kpi-label">CONDUCTORES ACTIVOS</div>
          <div class="op-kpi-value">{{ kpi.conductores_activos }}</div>
          <div class="op-kpi-sub">{{ kpi.conductores_mes_anterior }} el mes anterior</div>
        </div>
        <div class="op-kpi-card op-kpi-card--alert">
          <div class="op-kpi-top">
            <div class="op-kpi-icon op-icon-red"><i class="pi pi-exclamation-triangle"></i></div>
            <div class="op-kpi-badge op-badge-neutral"><i class="pi pi-ban"></i> No despachables</div>
          </div>
          <div class="op-kpi-label">ALERTAS CRÍTICAS</div>
          <div class="op-kpi-value op-value-red">{{ kpi.alertas_criticas }}</div>
          <div class="op-kpi-sub">SOAT vencido + licencia vencida/retenida</div>
        </div>
        <div class="op-kpi-card op-kpi-card--warn">
          <div class="op-kpi-top">
            <div class="op-kpi-icon op-icon-orange"><i class="pi pi-calendar-times"></i></div>
          </div>
          <div class="op-kpi-label">PRÓXIMOS VENCIMIENTOS</div>
          <div class="op-kpi-value op-value-orange">{{ kpi.vencen_30 }}</div>
          <div class="op-kpi-sub">SOAT · RTM · Licencias en 30 días</div>
        </div>
      </div>

      <!-- ── FILA 1: SOAT + RTM ── -->
      <div class="op-section-label">Indicadores de vehículos</div>
      <div class="op-charts-grid">
        <div class="op-chart-card">
          <div class="op-chart-header">
            <div><div class="op-chart-title">Estado del SOAT</div><div class="op-chart-sub">{{ kpi.vehiculos_activos }} vehículos registrados</div></div>
            <button class="op-link-btn" @click="abrirModalSoat">Ver listado <i class="pi pi-arrow-right"></i></button>
          </div>
          <div class="op-chart-body">
            <div class="op-donut-wrap">
              <canvas :ref="el => canvases.soat = el" width="200" height="200"></canvas>
              <div class="op-donut-center"><div class="op-donut-pct">{{ soatPct }}%</div><div class="op-donut-lbl">VIGENTE</div></div>
            </div>
            <div class="op-legend">
              <div class="op-legend-item" v-for="item in soatItems" :key="item.label">
                <div class="op-legend-dot" :style="{ background: item.color }"></div>
                <div class="op-legend-label">{{ item.label }}</div>
                <div class="op-legend-count">{{ item.count }}</div>
                <div class="op-legend-pct">{{ item.pct }}%</div>
              </div>
            </div>
          </div>
        </div>
        <div class="op-chart-card">
          <div class="op-chart-header">
            <div><div class="op-chart-title">Tecnomecánica (RTM)</div><div class="op-chart-sub">
                    {{ kpi.vehiculos_activos }} vehículos ·
                    <span v-if="kpi.rtm_exentos > 0" style="color:#6b7280">
                        {{ kpi.rtm_exentos }} exentos (≤2 años)
                    </span>
                    </div></div>
            <button class="op-link-btn" @click="abrirModalRtm">Ver listado <i class="pi pi-arrow-right"></i></button>
          </div>
          <div class="op-chart-body">
            <div class="op-donut-wrap">
              <canvas :ref="el => canvases.rtm = el" width="200" height="200"></canvas>
              <div class="op-donut-center"><div class="op-donut-pct">{{ rtmPct }}%</div><div class="op-donut-lbl">VIGENTE</div></div>
            </div>
            <div class="op-legend">
              <div class="op-legend-item" v-for="item in rtmItems" :key="item.label">
                <div class="op-legend-dot" :style="{ background: item.color }"></div>
                <div class="op-legend-label">{{ item.label }}</div>
                <div class="op-legend-count">{{ item.count }}</div>
                <div class="op-legend-pct">{{ item.pct }}%</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- FILA 2: Clase (barH) + Combustible (donut) -->
      <div class="op-charts-grid">
        <div class="op-chart-card">
          <div class="op-chart-header">
            <div><div class="op-chart-title">Vehículos por clase</div><div class="op-chart-sub">Click en barra para filtrar</div></div>
           <button class="op-link-btn" @click="abrirModalClase">Ver todos <i class="pi pi-arrow-right"></i></button>
          </div>
          <div class="op-bar-body"><canvas :ref="el => canvases.clase = el"></canvas></div>
        </div>
        <div class="op-chart-card">
          <div class="op-chart-header">
            <div><div class="op-chart-title">Distribución por combustible</div><div class="op-chart-sub">{{ kpi.vehiculos_activos }} vehículos</div></div>
          </div>
          <div class="op-chart-body">
            <div class="op-donut-wrap">
              <canvas :ref="el => canvases.combustible = el" width="200" height="200"></canvas>
              <div class="op-donut-center"><div class="op-donut-pct" style="font-size:1rem">{{ Object.keys(combustibleData)[0] || '—' }}</div></div>
            </div>
            <div class="op-legend">
              <div class="op-legend-item" v-for="(val, lbl) in combustibleData" :key="lbl">
                <div class="op-legend-dot" :style="{ background: combustibleColor(lbl) }"></div>
                <div class="op-legend-label">{{ lbl }}</div>
                <div class="op-legend-count">{{ val }}</div>
                <div class="op-legend-pct">{{ Math.round(val / (kpi.vehiculos_activos||1) * 100) }}%</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── FILA 3: Licencias + Antecedentes + Comparendos ── -->
      <div class="op-section-label">Indicadores de conductores</div>
      <div class="op-charts-grid">
        <div class="op-chart-card">
          <div class="op-chart-header">
            <div><div class="op-chart-title">Estado de licencias</div><div class="op-chart-sub">{{ kpi.conductores_activos }} conductores</div></div>
            <button class="op-link-btn" @click="abrirModalLicencias">Ver listado <i class="pi pi-arrow-right"></i></button>
          </div>
          <div class="op-chart-body">
            <div class="op-donut-wrap">
              <canvas :ref="el => canvases.licencias = el" width="200" height="200"></canvas>
              <div class="op-donut-center"><div class="op-donut-pct">{{ licenciasPct }}%</div><div class="op-donut-lbl">ACTIVAS</div></div>
            </div>
            <div class="op-legend">
              <div class="op-legend-item" v-for="item in licenciasItems" :key="item.label">
                <div class="op-legend-dot" :style="{ background: item.color }"></div>
                <div class="op-legend-label">{{ item.label }}</div>
                <div class="op-legend-count">{{ item.count }}</div>
                <div class="op-legend-pct">{{ item.pct }}%</div>
              </div>
            </div>
          </div>
        </div>
        <div class="op-chart-card">
          <div class="op-chart-header">
            <div><div class="op-chart-title">Antecedentes y compliance</div><div class="op-chart-sub">Policía, Contraloría y Procuraduría</div></div>
            <button class="op-link-btn" @click="abrirModalAntecedentes">Ver listado <i class="pi pi-arrow-right"></i></button>
          </div>
          <div class="op-chart-body">
            <div class="op-donut-wrap">
              <canvas :ref="el => canvases.antecedentes = el" width="200" height="200"></canvas>
              <div class="op-donut-center"><div class="op-donut-pct">{{ antecedentesOkPct }}%</div><div class="op-donut-lbl">SIN ANTEC.</div></div>
            </div>
            <div class="op-legend">
              <div class="op-legend-item" v-for="item in antecedentesItems" :key="item.label">
                <div class="op-legend-dot" :style="{ background: item.color }"></div>
                <div class="op-legend-label">{{ item.label }}</div>
                <div class="op-legend-count">{{ item.count }}</div>
                <div class="op-legend-pct">{{ item.pct }}%</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- FILA 4: Comparendos por rangos (barra vertical ocupa todo el ancho) -->
      <div class="op-charts-grid-single">
        <div class="op-chart-card">
          <div class="op-chart-header">
            <div><div class="op-chart-title">Conductores con comparendos SIMIT</div><div class="op-chart-sub">Agrupados por cantidad de comparendos</div></div>
            <button class="op-link-btn" @click="abrirModalComparendos">Ver listado <i class="pi pi-arrow-right"></i></button>
          </div>
          <div class="op-bar-body"><canvas :ref="el => canvases.comparendos = el"></canvas></div>
        </div>
      </div>

      <!-- ── TABLA ALERTAS CRÍTICAS ── -->
      <div class="op-alertas-card">
        <div class="op-alertas-header">
          <div>
            <div class="op-alertas-title">Alertas críticas</div>
            <div class="op-alertas-sub">Recursos que requieren atención inmediata · {{ alertasDetalle.length }} alertas activas</div>
          </div>
          <button class="op-link-btn" @click="abrirModalAlertas">Ver todas las alertas <i class="pi pi-arrow-right"></i></button>
        </div>
        <div class="op-alertas-table">
          <div class="op-alertas-thead">
            <div>TIPO</div>
            <div>RECURSO</div>
            <div>DETALLE</div>
            <div>ESTADO</div>
            <div>ACCIÓN</div>
          </div>
          <div v-if="!alertasDetalle.length" class="op-alertas-empty">
            <i class="pi pi-check-circle"></i> Sin alertas críticas activas
          </div>
          <div v-for="(alerta, i) in alertasDetalle.slice(0, 10)" :key="i" class="op-alertas-row">
            <div>
              <span :class="['op-alerta-badge', `op-alerta-badge--${alerta.color}`]">
                <i :class="badgeIcon(alerta.tipo)"></i> {{ alerta.label }}
              </span>
            </div>
            <div class="op-alerta-recurso">
              <div class="op-alerta-recurso-icon">
                <i :class="alerta.icono === 'vehiculo' ? 'pi pi-truck' : 'pi pi-user'"></i>
              </div>
              <div>
                <div class="op-alerta-recurso-name">{{ alerta.recurso }}</div>
                <div class="op-alerta-recurso-sub">{{ alerta.recurso_sub }}</div>
              </div>
            </div>
            <div class="op-alerta-detalle">{{ alerta.detalle }}</div>
            <div :class="['op-alerta-estado', `op-alerta-estado--${alerta.estado_color}`]">{{ alerta.estado }}</div>
            <div>
              <button class="op-alerta-ver-btn" @click="abrirModalAlerta(alerta)">
                Ver <i class="pi pi-arrow-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- ══ MODAL DETALLE ALERTA — VEHÍCULO ══ -->
    <Dialog v-model:visible="modalVehiculo.visible" :style="{ width: '520px' }" :modal="true" :header="modalVehiculo.data?.recurso + ' · ' + (modalVehiculo.data?.recurso_sub || '')" class="op-modal">
      <div v-if="modalVehiculo.data" class="op-modal-body">
        <p class="op-modal-intro">Vehículo en estado de alerta. Detalle:</p>
        <div class="op-modal-grid">
          <div class="op-modal-label">Tipo de alerta</div>  <div class="op-modal-val">{{ modalVehiculo.data.label }}</div>
          <div class="op-modal-label">CDA / Aseguradora</div> <div class="op-modal-val">{{ modalVehiculo.data.detalle }}</div>
          <div class="op-modal-label">Vencimiento</div>      <div class="op-modal-val">{{ modalVehiculo.data.fecha_fin || '—' }}</div>
          <div class="op-modal-label">Estado</div>           <div :class="['op-modal-val', 'op-modal-estado--' + modalVehiculo.data.estado_color]">{{ modalVehiculo.data.estado }}</div>
          <div class="op-modal-label">Acción sugerida</div>  <div class="op-modal-val">{{ accionSugerida(modalVehiculo.data) }}</div>
        </div>
      </div>
      <template #footer>
        <Button label="Cerrar" class="p-button-text" @click="modalVehiculo.visible = false" />
        <Button label="Abrir hoja de vida" icon="pi pi-external-link" class="op-btn-navy" @click="$emit('ver-hv-vehiculo', modalVehiculo.data); modalVehiculo.visible = false" />
      </template>
    </Dialog>

    <!-- ══ MODAL DETALLE ALERTA — CONDUCTOR ══ -->
    <Dialog v-model:visible="modalConductor.visible" :style="{ width: '520px' }" :modal="true" :header="modalConductor.data?.recurso" class="op-modal">
      <div v-if="modalConductor.data" class="op-modal-body">
        <p class="op-modal-intro">Conductor en estado de alerta. Detalle:</p>
        <div class="op-modal-grid">
          <div class="op-modal-label">Documento</div>       <div class="op-modal-val">{{ modalConductor.data.recurso_sub?.split('·')[0]?.trim() }}</div>
          <div class="op-modal-label">Licencia</div>        <div class="op-modal-val">{{ modalConductor.data.detalle_licencia || modalConductor.data.detalle }}</div>
          <div class="op-modal-label">Categoría</div>       <div class="op-modal-val">{{ modalConductor.data.categoria || '—' }}</div>
          <div class="op-modal-label">Restricciones</div>   <div class="op-modal-val">{{ modalConductor.data.restricciones || 'Ninguna' }}</div>
          <div class="op-modal-label">Organismo</div>       <div class="op-modal-val">{{ modalConductor.data.detalle }}</div>
          <div class="op-modal-label">Comparendos</div>     <div class="op-modal-val">{{ modalConductor.data.comparendos_txt || 'Sin comparendos activos' }}</div>
          <div class="op-modal-label">Estado operativo</div><div :class="['op-modal-val op-modal-estado--' + modalConductor.data.estado_color]">{{ estadoOperativo(modalConductor.data) }}</div>
        </div>
      </div>
      <template #footer>
        <Button label="Cerrar" class="p-button-text" @click="modalConductor.visible = false" />
        <Button label="Abrir hoja de vida" icon="pi pi-external-link" class="op-btn-navy" @click="$emit('ver-hv-conductor', modalConductor.data); modalConductor.visible = false" />
      </template>
    </Dialog>
    <!-- ══ MODAL SOAT ══ -->
    <Dialog v-model:visible="modalSoat.visible" :style="{width:'95vw',maxWidth:'1100px'}"
            header="Estado del SOAT · Listado completo" :modal="true" class="op-modal op-modal-tabla">
      <div v-if="modalSoat.loading" class="op-modal-loading">
        <i class="pi pi-spin pi-spinner"></i> Cargando datos...
      </div>
      <template v-else>
        <div class="op-modal-resumen">
          <span class="op-resumen-badge op-resumen-badge--verde">
            Vigentes: {{ modalSoat.rows.filter(r=>r.estado==='vigente').length }}
          </span>
          <span class="op-resumen-badge op-resumen-badge--naranja">
            Por vencer: {{ modalSoat.rows.filter(r=>r.estado==='por_vencer').length }}
          </span>
          <span class="op-resumen-badge op-resumen-badge--rojo">
            Vencidos: {{ modalSoat.rows.filter(r=>r.estado==='vencido').length }}
          </span>
          <span class="op-resumen-total">Total: {{ modalSoat.rows.length }}</span>
        </div>
        <div class="op-tabla-wrap">
          <table class="op-tabla">
            <thead>
              <tr>
                <th>Placa</th>
                <th>Vehículo</th>
                <th>Clase</th>
                <th>Aseguradora</th>
                <th>Vence</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, i) in modalSoat.rows" :key="i">
                <td><strong>{{ r.placa }}</strong></td>
                <td>{{ r.marca }}</td>
                <td>{{ r.clase || '—' }}</td>
                <td>{{ r.aseguradora }}</td>
                <td>{{ fmtFecha(r.fecha_fin) }}</td>
                <td><span :class="['op-badge-estado', estadoBadgeClass(r.estado)]">{{ estadoLabel(r.estado) }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
      <template #footer>
        <Button label="Cerrar" icon="pi pi-times" class="p-button-text" @click="modalSoat.visible=false"/>
      </template>
    </Dialog>

    <!-- ══ MODAL RTM ══ -->
    <Dialog v-model:visible="modalRtm.visible" :style="{width:'95vw',maxWidth:'1100px'}"
            header="Tecnomecánica (RTM) · Listado completo" :modal="true" class="op-modal op-modal-tabla">
      <div v-if="modalRtm.loading" class="op-modal-loading">
        <i class="pi pi-spin pi-spinner"></i> Cargando datos...
      </div>
      <template v-else>
        <div class="op-modal-resumen">
          <span class="op-resumen-badge op-resumen-badge--verde">
            Vigentes: {{ modalRtm.rows.filter(r=>r.estado==='vigente').length }}
          </span>
          <span class="op-resumen-badge op-resumen-badge--naranja">
            Por vencer: {{ modalRtm.rows.filter(r=>r.estado==='por_vencer').length }}
          </span>
          <span class="op-resumen-badge op-resumen-badge--rojo">
            Vencidos: {{ modalRtm.rows.filter(r=>r.estado==='vencido').length }}
          </span>
          <span class="op-resumen-badge op-resumen-badge--gris">
            Exentos: {{ modalRtm.rows.filter(r=>r.estado==='exento').length }}
          </span>
          <span class="op-resumen-total">Total: {{ modalRtm.rows.length }}</span>
        </div>
        <div class="op-tabla-wrap">
          <table class="op-tabla">
            <thead>
              <tr>
                <th>Placa</th>
                <th>Vehículo</th>
                <th>Clase</th>
                <th>Año modelo</th>
                <th>CDA</th>
                <th>Vence</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, i) in modalRtm.rows" :key="i">
                <td><strong>{{ r.placa }}</strong></td>
                <td>{{ r.marca }}</td>
                <td>{{ r.clase || '—' }}</td>
                <td>{{ r.año_modelo }}</td>
                <td>{{ r.cda }}</td>
                <td>{{ fmtFecha(r.fecha_fin) }}</td>
                <td><span :class="['op-badge-estado', estadoBadgeClass(r.estado)]">{{ estadoLabel(r.estado) }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
      <template #footer>
        <Button label="Cerrar" icon="pi pi-times" class="p-button-text" @click="modalRtm.visible=false"/>
      </template>
    </Dialog>

    <!-- ══ MODAL LICENCIAS ══ -->
    <Dialog v-model:visible="modalLicencias.visible" :style="{width:'95vw',maxWidth:'1100px'}"
            header="Estado de licencias · Listado completo" :modal="true" class="op-modal op-modal-tabla">
      <div v-if="modalLicencias.loading" class="op-modal-loading">
        <i class="pi pi-spin pi-spinner"></i> Cargando datos...
      </div>
      <template v-else>
        <div class="op-modal-resumen">
          <span class="op-resumen-badge op-resumen-badge--verde">
            Activas: {{ modalLicencias.rows.filter(r=>r.estado==='activa').length }}
          </span>
          <span class="op-resumen-badge op-resumen-badge--naranja">
            Por vencer: {{ modalLicencias.rows.filter(r=>r.estado==='por_vencer').length }}
          </span>
          <span class="op-resumen-badge op-resumen-badge--naranja">
            Retenidas: {{ modalLicencias.rows.filter(r=>r.estado==='retenida').length }}
          </span>
          <span class="op-resumen-badge op-resumen-badge--rojo">
            Vencidas: {{ modalLicencias.rows.filter(r=>r.estado==='vencida').length }}
          </span>
          <span class="op-resumen-total">Total: {{ modalLicencias.rows.length }}</span>
        </div>
        <div class="op-tabla-wrap">
          <table class="op-tabla">
            <thead>
              <tr>
                <th>Documento</th>
                <th>Conductor</th>
                <th>Categoría</th>
                <th>Entidad</th>
                <th>Vence</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, i) in modalLicencias.rows" :key="i">
                <td>{{ r.documento }}</td>
                <td><strong>{{ r.nombre }}</strong></td>
                <td>{{ r.categoria }}</td>
                <td>{{ r.entidad }}</td>
                <td>{{ fmtFecha(r.fecha_ven) }}</td>
                <td><span :class="['op-badge-estado', estadoBadgeClass(r.estado)]">{{ estadoLabel(r.estado) }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
      <template #footer>
        <Button label="Cerrar" icon="pi pi-times" class="p-button-text" @click="modalLicencias.visible=false"/>
      </template>
    </Dialog>

    <!-- ══ MODAL ANTECEDENTES ══ -->
    <Dialog v-model:visible="modalAntecedentes.visible" :style="{width:'95vw',maxWidth:'1100px'}"
            header="Antecedentes y compliance · Listado completo" :modal="true" class="op-modal op-modal-tabla">
      <div v-if="modalAntecedentes.loading" class="op-modal-loading">
        <i class="pi pi-spin pi-spinner"></i> Cargando datos...
      </div>
      <template v-else>
        <div class="op-modal-resumen">
          <span class="op-resumen-badge op-resumen-badge--verde">
            Sin alertas: {{ modalAntecedentes.rows.filter(r=>r.estado==='sin_alerta').length }}
          </span>
          <span class="op-resumen-badge op-resumen-badge--naranja">
            Con comparendos: {{ modalAntecedentes.rows.filter(r=>r.estado==='con_comparendos').length }}
          </span>
          <span class="op-resumen-badge op-resumen-badge--rojo">
            Con antecedentes: {{ modalAntecedentes.rows.filter(r=>r.estado==='con_antecedentes').length }}
          </span>
          <span class="op-resumen-total">Total: {{ modalAntecedentes.rows.length }}</span>
        </div>
        <div class="op-tabla-wrap">
          <table class="op-tabla">
            <thead>
              <tr>
                <th>Documento</th>
                <th>Conductor</th>
                <th>Policía</th>
                <th>Contraloría</th>
                <th>Comparendos</th>
                <th>Total multas</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, i) in modalAntecedentes.rows" :key="i">
                <td>{{ r.documento }}</td>
                <td><strong>{{ r.nombre }}</strong></td>
                <td>{{ r.policia }}</td>
                <td>{{ r.contraloria }}</td>
                <td>{{ r.comparendos }}</td>
                <td>{{ r.total_multas ? fmtPesos(r.total_multas) : '—' }}</td>
                <td><span :class="['op-badge-estado', estadoBadgeClass(r.estado)]">{{ estadoLabel(r.estado) }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
      <template #footer>
        <Button label="Cerrar" icon="pi pi-times" class="p-button-text" @click="modalAntecedentes.visible=false"/>
      </template>
    </Dialog>
    <!-- ══ MODAL VEHÍCULOS POR CLASE ══ -->
    <Dialog v-model:visible="modalClase.visible" :style="{width:'95vw',maxWidth:'1200px'}"
            header="Vehículos por clase · Listado completo" :modal="true" class="op-modal op-modal-tabla">
      <div v-if="modalClase.loading" class="op-modal-loading">
        <i class="pi pi-spin pi-spinner"></i> Cargando datos...
      </div>
      <template v-else>
        <div class="op-modal-resumen">
          <!-- Chips filtrables por clase -->
          <button
            v-for="cls in clasesUnicas()" :key="cls"
            :class="['op-chip-filtro', modalClase.filtroClase === cls ? 'op-chip-filtro--activo' : '']"
            @click="modalClase.filtroClase = (modalClase.filtroClase === cls ? '' : cls)"
          >
            {{ cls }}
            <span class="op-chip-count">{{ modalClase.rows.filter(r=>r.clase===cls).length }}</span>
          </button>
          <span class="op-resumen-total">
            {{ clasesFiltradas().length }} de {{ modalClase.rows.length }} vehículos
          </span>
        </div>
        <div class="op-tabla-wrap">
          <table class="op-tabla">
            <thead>
              <tr>
                <th>Placa</th>
                <th>Marca / Línea</th>
                <th>Clase</th>
                <th>Modelo</th>
                <th>Color</th>
                <th>Combustible</th>
                <th>Cilindraje</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, i) in clasesFiltradas()" :key="i">
                <td><strong>{{ r.placa }}</strong></td>
                <td>{{ r.marca }}</td>
                <td>
                  <span class="op-badge-clase">{{ r.clase }}</span>
                </td>
                <td>{{ r.modelo }}</td>
                <td>{{ r.color }}</td>
                <td>{{ r.combustible }}</td>
                <td>{{ r.cilindraje }}</td>
              </tr>
              <tr v-if="!clasesFiltradas().length">
                <td colspan="7" class="op-tabla-empty">Sin resultados para este filtro</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
      <template #footer>
        <Button label="Cerrar" icon="pi pi-times" class="p-button-text" @click="modalClase.visible=false"/>
      </template>
    </Dialog>

    <!-- ══ MODAL COMPARENDOS SIMIT ══ -->
    <Dialog v-model:visible="modalComparendos.visible" :style="{width:'95vw',maxWidth:'1000px'}"
            header="Comparendos SIMIT · Listado completo" :modal="true" class="op-modal op-modal-tabla">
      <div v-if="modalComparendos.loading" class="op-modal-loading">
        <i class="pi pi-spin pi-spinner"></i> Cargando datos...
      </div>
      <template v-else>
        <div class="op-modal-resumen">
          <button
            v-for="rng in ['Sin comparendos','1–3','4–10','+10']" :key="rng"
            :class="['op-chip-filtro', modalComparendos.filtroRango === rng ? 'op-chip-filtro--activo' : '']"
            @click="modalComparendos.filtroRango = (modalComparendos.filtroRango === rng ? '' : rng)"
          >
            {{ rng }}
            <span class="op-chip-count">{{ modalComparendos.rows.filter(r=>r.rango===rng).length }}</span>
          </button>
          <span class="op-resumen-total">
            Total multas activas:
            <strong>{{ fmtPesos(modalComparendos.rows.reduce((a,r)=>a+(r.total_multas||0),0)) }}</strong>
          </span>
        </div>
        <div class="op-tabla-wrap">
          <table class="op-tabla">
            <thead>
              <tr>
                <th>Documento</th>
                <th>Conductor</th>
                <th># Comparendos</th>
                <th>Total multas</th>
                <th>Rango</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, i) in comparendosFiltrados()" :key="i">
                <td>{{ r.documento }}</td>
                <td><strong>{{ r.nombre }}</strong></td>
                <td>
                  <span :class="['op-num-comp', r.comparendos > 0 ? 'op-num-comp--alerta' : '']">
                    {{ r.comparendos }}
                  </span>
                </td>
                <td>{{ r.total_multas ? fmtPesos(r.total_multas) : '—' }}</td>
                <td>
                  <span :class="['op-badge-estado', rangoClass(r.rango)]">{{ r.rango }}</span>
                </td>
              </tr>
              <tr v-if="!comparendosFiltrados().length">
                <td colspan="5" class="op-tabla-empty">Sin conductores en este rango</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
      <template #footer>
        <Button label="Cerrar" icon="pi pi-times" class="p-button-text" @click="modalComparendos.visible=false"/>
      </template>
    </Dialog>

    <!-- ══ MODAL ALERTAS CRÍTICAS (usa datos ya cargados) ══ -->
    <Dialog v-model:visible="modalAlertas.visible" :style="{width:'95vw',maxWidth:'1300px'}"
            header="Alertas críticas · Listado completo" :modal="true" class="op-modal op-modal-tabla">
      <div class="op-modal-resumen">
        <button
          v-for="f in [{k:'rojo',l:'Críticas'},{k:'naranja',l:'Advertencias'},{k:'azul',l:'Comparendos'}]"
          :key="f.k"
          :class="['op-chip-filtro', `op-chip-filtro--${f.k}`, modalAlertas.filtroColor === f.k ? 'op-chip-filtro--activo' : '']"
          @click="modalAlertas.filtroColor = (modalAlertas.filtroColor === f.k ? '' : f.k)"
        >
          {{ f.l }}
          <span class="op-chip-count">{{ alertasDetalle.filter(a=>a.color===f.k).length }}</span>
        </button>
        <span class="op-resumen-total">{{ alertasFiltradas().length }} alertas</span>
      </div>
      <div class="op-tabla-wrap">
        <table class="op-tabla">
          <thead>
            <tr>
              <th>Tipo</th>
              <th>Recurso</th>
              <th>Detalle</th>
              <th>Estado</th>
              <th>Acción</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(alerta, i) in alertasFiltradas()" :key="i">
              <td>
                <span :class="['op-alerta-badge', `op-alerta-badge--${alerta.color}`]">
                  <i :class="badgeIcon(alerta.tipo)"></i> {{ alerta.label }}
                </span>
              </td>
              <td>
                <div class="op-alerta-recurso">
                  <div class="op-alerta-recurso-icon">
                    <i :class="alerta.icono === 'vehiculo' ? 'pi pi-truck' : 'pi pi-user'"></i>
                  </div>
                  <div>
                    <div class="op-alerta-recurso-name">{{ alerta.recurso }}</div>
                    <div class="op-alerta-recurso-sub">{{ alerta.recurso_sub }}</div>
                  </div>
                </div>
              </td>
              <td class="op-alerta-detalle">{{ alerta.detalle }}</td>
              <td :class="['op-alerta-estado', `op-alerta-estado--${alerta.estado_color}`]">
                {{ alerta.estado }}
              </td>
              <td>
                <button class="op-alerta-ver-btn" @click="abrirModalAlerta(alerta); modalAlertas.visible=false">
                  Ver <i class="pi pi-arrow-right"></i>
                </button>
              </td>
            </tr>
            <tr v-if="!alertasFiltradas().length">
              <td colspan="5" class="op-tabla-empty">
                <i class="pi pi-check-circle" style="color:#22c55e;margin-right:.5rem"></i>
                Sin alertas en esta categoría
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <template #footer>
        <Button label="Cerrar" icon="pi pi-times" class="p-button-text" @click="modalAlertas.visible=false"/>
      </template>
    </Dialog>
  </div>
</template>

<script>
export default {
  name: 'PanelOperativo',
  emits: ['ver-soat','ver-rtm','ver-licencias','ver-antecedentes','ver-vehiculos','ver-comparendos','ver-alertas','filtrar-clase','ver-hv-vehiculo','ver-hv-conductor'],

  data() {
    return {
      loader: false,
      lastUpdate: null,
      autoRefresh: false,
      rtm_exentos: 0,
      autoRefreshTimer: null,
      canvases: { soat:null, rtm:null, licencias:null, antecedentes:null, clase:null, combustible:null, comparendos:null },
      charts:   { soat:null, rtm:null, licencias:null, antecedentes:null, clase:null, combustible:null, comparendos:null },
      kpi: { vehiculos_activos:0, vehiculos_mes_anterior:0, vehiculos_delta:null,
             conductores_activos:0, conductores_mes_anterior:0, conductores_delta:null,
             alertas_criticas:0, vencen_30:0 },
      soatData:         { vigente:0, por_vencer:0, vencido:0 },
      rtmData:          { vigente:0, por_vencer:0, vencido:0 },
      licenciasData:    { activa:0, por_vencer:0, vencida:0, retenida:0 },
      antecedentesData: { sin_alerta:0, comparendos:0, antecedentes:0 },
      claseData:        {},
      combustibleData:  {},
      comparendosRangos:{},
      alertasDetalle:   [],
      modalVehiculo:  { visible: false, data: null },
      modalConductor: { visible: false, data: null },
      C: { verde:'#22c55e', naranja:'#f97316', rojo:'#ef4444', gris:'#94a3b8',
           azul1:'#1e3a5f', azul2:'#2d5986', azul3:'#3a7ab3', azul4:'#4a9ad4' },
      COMB_COLORS: { DIESEL:'#1e3a5f', GASOLINA:'#2d5986', GNV:'#3a7ab3', ELECTRICO:'#4a9ad4' },
      // Modales de listado completo
      modalSoat:         { visible: false, loading: false, rows: [] },
      modalRtm:          { visible: false, loading: false, rows: [] },
      modalLicencias:    { visible: false, loading: false, rows: [] },
      modalAntecedentes: { visible: false, loading: false, rows: [] },
      modalClase:        { visible: false, loading: false, rows: [], filtroClase: '' },
      modalComparendos:  { visible: false, loading: false, rows: [], filtroRango: '' },
      modalAlertas:      { visible: false, filtroColor: '' },
    };
  },

  computed: {
    lastUpdateLabel() {
      if (!this.lastUpdate) return '';
      const diff = Math.round((Date.now() - this.lastUpdate) / 60000);
      if (diff < 1) return 'hace menos de 1 min';
      if (diff === 1) return 'hace 1 min';
      return `hace ${diff} min`;
    },
    soatTotal()  { const d=this.soatData;  return d.vigente+d.por_vencer+d.vencido||1; },
    rtmTotal()   { const d=this.rtmData;   return d.vigente+d.por_vencer+d.vencido||1; },
    licTotal()   { const d=this.licenciasData; return d.activa+d.por_vencer+d.vencida+d.retenida||1; },
    antTotal()   { const d=this.antecedentesData; return d.sin_alerta+d.comparendos+d.antecedentes||1; },
    soatPct()           { return Math.round(this.soatData.vigente/this.soatTotal*100); },
    rtmPct()            { return Math.round(this.rtmData.vigente/this.rtmTotal*100); },
    licenciasPct()      { return Math.round(this.licenciasData.activa/this.licTotal*100); },
    antecedentesOkPct() { return Math.round(this.antecedentesData.sin_alerta/this.antTotal*100); },
    soatItems() {
      const d=this.soatData,t=this.soatTotal;
      return [{label:'Vigente',count:d.vigente,pct:Math.round(d.vigente/t*100),color:this.C.verde},
              {label:'Por vencer ≤30d',count:d.por_vencer,pct:Math.round(d.por_vencer/t*100),color:this.C.naranja},
              {label:'Vencido',count:d.vencido,pct:Math.round(d.vencido/t*100),color:this.C.rojo}];
    },
    rtmItems() {
      const d=this.rtmData,t=this.rtmTotal;
      return [{label:'Vigente',count:d.vigente,pct:Math.round(d.vigente/t*100),color:this.C.verde},
              {label:'Por vencer ≤30d',count:d.por_vencer,pct:Math.round(d.por_vencer/t*100),color:this.C.naranja},
              {label:'Vencido',count:d.vencido,pct:Math.round(d.vencido/t*100),color:this.C.rojo}];
    },
    licenciasItems() {
      const d=this.licenciasData,t=this.licTotal;
      return [{label:'Activa',count:d.activa,pct:Math.round(d.activa/t*100),color:this.C.verde},
              {label:'Vencida',count:d.vencida,pct:Math.round(d.vencida/t*100),color:this.C.rojo},
              {label:'Retenida',count:d.retenida,pct:Math.round(d.retenida/t*100),color:this.C.naranja}
             ].filter(i=>i.count>0);
    },
    antecedentesItems() {
      const d=this.antecedentesData,t=this.antTotal;
      return [{label:'Sin antecedentes',count:d.sin_alerta,pct:Math.round(d.sin_alerta/t*100),color:this.C.verde},
              {label:'Con antecedentes',count:d.antecedentes,pct:Math.round(d.antecedentes/t*100),color:this.C.rojo},
              {label:'Con comparendos',count:d.comparendos,pct:Math.round(d.comparendos/t*100),color:this.C.naranja}
             ].filter(i=>i.count>0);
    },
  },

  mounted() { this.cargarDatos(); },
  beforeUnmount() {
    Object.values(this.charts).forEach(c=>c?.destroy?.());
    if (this.autoRefreshTimer) clearInterval(this.autoRefreshTimer);
  },

  methods: {
    toggleAutoRefresh() {
      if (this.autoRefreshTimer) { clearInterval(this.autoRefreshTimer); this.autoRefreshTimer = null; }
      if (this.autoRefresh) {
        this.autoRefreshTimer = setInterval(() => this.cargarDatos(), 5 * 60 * 1000);
      }
    },

    cargarDatos() {
      this.loader = true;
      const cookie = {};
      document.cookie.split(';').forEach(el => { const [k,v]=el.split('='); cookie[k.trim()]=decodeURIComponent(v??''); });
      window.api.call('post','/dashboard_operativo_hv',{ email:cookie['Email'], id_rol:cookie['IdRol'] })
        .then(res => {
          if (res.status===200) {
            const d=res.data.data;
            this.kpi = {
              vehiculos_activos:        d.vehiculos_activos        ??0,
              vehiculos_mes_anterior:   d.vehiculos_mes_anterior   ??0,
              vehiculos_delta:          d.vehiculos_delta          ??null,
              conductores_activos:      d.conductores_activos      ??0,
              conductores_mes_anterior: d.conductores_mes_anterior ??0,
              conductores_delta:        d.conductores_delta        ??null,
              alertas_criticas:         d.alertas_criticas         ??0,
              vencen_30:                d.vencen_30                ??0,
            };
            this.soatData         = d.soat         ??{vigente:0,por_vencer:0,vencido:0};
            this.rtmData          = d.rtm          ??{vigente:0,por_vencer:0,vencido:0};
            this.licenciasData    = d.licencias    ??{activa:0,por_vencer:0,vencida:0,retenida:0};
            this.antecedentesData = d.antecedentes ??{sin_alerta:0,comparendos:0,antecedentes:0};
            this.claseData        = d.por_clase    ??{};
            this.combustibleData  = d.combustible  ??{};
            this.comparendosRangos= d.comparendos_rangos ??{};
            this.alertasDetalle   = d.alertas_detalle ??[];
            this.rtm_exentos      = d.rtm_exentos ?? 0;
            this.lastUpdate       = Date.now();
            this.$nextTick(() => {
              this.donut('soat',   [this.soatData.vigente,this.soatData.por_vencer,this.soatData.vencido],[this.C.verde,this.C.naranja,this.C.rojo]);
              this.donut('rtm',    [this.rtmData.vigente,this.rtmData.por_vencer,this.rtmData.vencido],[this.C.verde,this.C.naranja,this.C.rojo]);
              this.donut('licencias',[this.licenciasData.activa,this.licenciasData.vencida,this.licenciasData.retenida],[this.C.verde,this.C.rojo,this.C.naranja]);
              this.donut('antecedentes',[this.antecedentesData.sin_alerta,this.antecedentesData.antecedentes,this.antecedentesData.comparendos],[this.C.verde,this.C.rojo,this.C.naranja]);
              // Combustible como donut
              const combVals = Object.values(this.combustibleData);
              const combCols = Object.keys(this.combustibleData).map(k => this.combustibleColor(k));
              this.donut('combustible', combVals, combCols);
              this.$nextTick(() => this._renderConReintento());
            });
          }
        })
        .catch(() => this._demo())
        .finally(() => { this.loader = false; });
    },

    _demo() {
      this.kpi = { vehiculos_activos:142, vehiculos_mes_anterior:137, vehiculos_delta:5.2,
                   conductores_activos:87, conductores_mes_anterior:88, conductores_delta:-1.1,
                   alertas_criticas:12, vencen_30:23 };
      this.soatData         = {vigente:118,por_vencer:12,vencido:12};
      this.rtmData          = {vigente:105,por_vencer:18,vencido:19};
      this.licenciasData    = {activa:71,por_vencer:0,vencida:11,retenida:5};
      this.antecedentesData = {sin_alerta:79,comparendos:0,antecedentes:8};
      this.claseData        = {'Camion':65,'Tractocamion':42,'Camioneta':22,'Motocicleta':15,'Furgon':3};
      this.combustibleData  = {'DIESEL':88,'GASOLINA':37,'GNV':14,'ELECTRICO':3};
      this.comparendosRangos= {'Sin comparendos':62,'1–3':18,'4–10':5,'+10':2};
      this.alertasDetalle   = [
        {tipo:'soat_vencido',label:'SOAT vencido',color:'rojo',icono:'vehiculo',recurso:'POL420',recurso_sub:'Chevrolet · 4x2 · Liviano',detalle:'La Previsora S.A.',estado:'Hace 5 días',estado_color:'rojo',fecha_fin:'2026-05-09'},
        {tipo:'licencia_vencida',label:'Licencia vencida',color:'rojo',icono:'conductor',recurso:'Juan Pérez Ramírez',recurso_sub:'CC 1020437518 · Cat. B2',detalle:'STRIA TTE BELLO',estado:'Hace 2 días',estado_color:'rojo',detalle_licencia:'Vencida hace 2 días',categoria:'B2',restricciones:'Conducir con lentes',comparendos_txt:'Sin comparendos activos'},
        {tipo:'rtm_por_vencer',label:'RTM por vencer',color:'naranja',icono:'vehiculo',recurso:'POL421',recurso_sub:'Volvo FH · Tractocamión',detalle:'CDA Bogotá Norte',estado:'En 3 días',estado_color:'naranja',fecha_fin:'2026-05-17'},
        {tipo:'licencia_retenida',label:'Licencia retenida',color:'naranja',icono:'conductor',recurso:'Carlos López Mejía',recurso_sub:'CC 70045918 · Cat. C2',detalle:'Comparendo activo',estado:'Sin fecha',estado_color:'naranja',detalle_licencia:'Retenida',categoria:'C2',restricciones:'Ninguna',comparendos_txt:'1 comparendo activo'},
        {tipo:'soat_por_vencer',label:'SOAT por vencer',color:'naranja',icono:'vehiculo',recurso:'POL422',recurso_sub:'Renault Trafic · Camioneta',detalle:'Sura Seguros',estado:'En 8 días',estado_color:'naranja',fecha_fin:'2026-05-22'},
        {tipo:'comparendos',label:'Comparendos +5',color:'azul',icono:'conductor',recurso:'Andrés Gómez Vargas',recurso_sub:'CC 80123456 · Cat. B1',detalle:'SIMIT · 7 multas',estado:'$2.450.000',estado_color:'rojo',comparendos_txt:'7 multas activas'},
      ];
      this.lastUpdate = Date.now();
      this.$nextTick(() => {
        this.donut('soat',[118,12,12],[this.C.verde,this.C.naranja,this.C.rojo]);
        this.donut('rtm',[105,18,19],[this.C.verde,this.C.naranja,this.C.rojo]);
        this.donut('licencias',[71,11,5],[this.C.verde,this.C.rojo,this.C.naranja]);
        this.donut('antecedentes',[79,8,0],[this.C.verde,this.C.rojo,this.C.naranja]);
        this.donut('combustible',[88,37,14,3],[this.C.azul1,this.C.azul2,this.C.azul3,this.C.azul4]);
        this.$nextTick(() => this._renderConReintento());
      });
    },

    _renderConReintento(intentos = 0) {
      const cvClase = this.canvases.clase;
      const cvComp  = this.canvases.comparendos;
      const aClase  = cvClase ? (cvClase.offsetWidth || cvClase.parentElement?.offsetWidth || 0) : 0;
      const aComp   = cvComp  ? (cvComp.offsetWidth  || cvComp.parentElement?.offsetWidth  || 0) : 0;
      if ((aClase > 10 && aComp > 10) || intentos >= 10) {
        this.barH('clase', this.claseData);
        this.barV('comparendos', this.comparendosRangos);
      } else {
        requestAnimationFrame(() => this._renderConReintento(intentos + 1));
      }
    },

    combustibleColor(lbl) {
      const map = this.COMB_COLORS;
      return map[lbl.toUpperCase()] || this.C.gris;
    },

    badgeIcon(tipo) {
      return { soat_vencido:'pi pi-shield', soat_por_vencer:'pi pi-clock',
               rtm_vencido:'pi pi-wrench', rtm_por_vencer:'pi pi-clock',
               licencia_vencida:'pi pi-id-card', licencia_retenida:'pi pi-lock',
               comparendos:'pi pi-file' }[tipo] || 'pi pi-exclamation-circle';
    },

    accionSugerida(alerta) {
      if (!alerta) return '—';
      if (alerta.tipo.includes('vencido'))    return 'Renovar inmediatamente';
      if (alerta.tipo.includes('por_vencer')) return 'Programar renovación esta semana';
      if (alerta.tipo.includes('retenida'))   return 'Gestionar liberación en organismo competente';
      return 'Revisar hoja de vida';
    },

    estadoOperativo(alerta) {
      if (!alerta) return '—';
      if (alerta.tipo === 'licencia_vencida') return 'No asignable';
      if (alerta.tipo === 'comparendos')      return 'Asignable con observación';
      return 'Asignable con observación';
    },

    abrirModalAlerta(alerta) {
      if (alerta.icono === 'vehiculo') {
        this.modalVehiculo.data    = alerta;
        this.modalVehiculo.visible = true;
      } else {
        this.modalConductor.data    = alerta;
        this.modalConductor.visible = true;
      }
    },

    // ── Canvas helpers ──────────────────────────────────────────────────────
    donut(key, vals, cols) {
      const cv=this.canvases[key]; if(!cv)return;
      this.charts[key]?.destroy?.();
      const ctx=cv.getContext('2d'),W=cv.width,H=cv.height,cx=W/2,cy=H/2,r=Math.min(W,H)/2-8,ri=r*.62;
      const total=vals.reduce((a,b)=>a+b,0)||1;
      ctx.clearRect(0,0,W,H);
      ctx.shadowColor='rgba(0,0,0,.06)'; ctx.shadowBlur=6;
      let a=-Math.PI/2;
      vals.forEach((v,i)=>{ if(v<=0)return; const s=(v/total)*2*Math.PI; ctx.beginPath(); ctx.moveTo(cx,cy); ctx.arc(cx,cy,r,a,a+s); ctx.closePath(); ctx.fillStyle=cols[i]||'#ccc'; ctx.fill(); a+=s; });
      ctx.shadowBlur=0;
      ctx.beginPath(); ctx.arc(cx,cy,ri,0,2*Math.PI); ctx.fillStyle='#fff'; ctx.fill();
      ctx.beginPath(); ctx.arc(cx,cy,ri,0,2*Math.PI); ctx.strokeStyle='rgba(0,0,0,.04)'; ctx.lineWidth=1; ctx.stroke();
      this.charts[key]={destroy:()=>ctx.clearRect(0,0,W,H)};
    },

    barH(key, obj) {
      const cv=this.canvases[key]; if(!cv)return;
      this.charts[key]?.destroy?.();
      const labels=Object.keys(obj),vals=Object.values(obj); if(!labels.length)return;
      const BH=28,GAP=14,PL=110,PR=40,PT=16,PB=28;
      const totalH=PT+labels.length*(BH+GAP)+PB;
      const ancho=cv.offsetWidth||cv.parentElement?.offsetWidth||cv.parentElement?.parentElement?.offsetWidth||460;
      cv.width=ancho; cv.height=totalH;
      const ctx=cv.getContext('2d'),W=cv.width,dW=W-PL-PR,maxV=Math.max(...vals)||1;
      ctx.clearRect(0,0,W,totalH);
      const steps=5;
      for(let i=0;i<=steps;i++){
        const x=PL+(dW/steps)*i; ctx.strokeStyle='#e8eaed'; ctx.lineWidth=1;
        ctx.beginPath(); ctx.moveTo(x,PT); ctx.lineTo(x,totalH-PB); ctx.stroke();
        ctx.fillStyle='#9ca3af'; ctx.font='11px system-ui'; ctx.textAlign='center';
        ctx.fillText(Math.round((maxV/steps)*i),x,totalH-8);
      }
      labels.forEach((lbl,i)=>{
        const y=PT+i*(BH+GAP),bW=(vals[i]/maxV)*dW,r=4;
        ctx.fillStyle='#374151'; ctx.font='12px system-ui'; ctx.textAlign='right'; ctx.fillText(lbl,PL-8,y+BH/2+4);
        ctx.fillStyle='#1e3a5f';
        ctx.beginPath(); ctx.moveTo(PL+r,y); ctx.lineTo(PL+bW-r,y); ctx.quadraticCurveTo(PL+bW,y,PL+bW,y+r);
        ctx.lineTo(PL+bW,y+BH-r); ctx.quadraticCurveTo(PL+bW,y+BH,PL+bW-r,y+BH);
        ctx.lineTo(PL+r,y+BH); ctx.quadraticCurveTo(PL,y+BH,PL,y+BH-r); ctx.lineTo(PL,y+r); ctx.quadraticCurveTo(PL,y,PL+r,y);
        ctx.closePath(); ctx.fill();
        if(bW>20){ ctx.fillStyle='#6b7280'; ctx.font='11px system-ui'; ctx.textAlign='left'; ctx.fillText(vals[i],PL+bW+5,y+BH/2+4); }
      });
      const self=this;
      const ch=(e)=>{
        const rect=cv.getBoundingClientRect(),mx=e.clientX-rect.left,my=e.clientY-rect.top;
        labels.forEach((lbl,i)=>{ const y=PT+i*(BH+GAP),bW=(vals[i]/maxV)*dW; if(mx>=PL&&mx<=PL+bW&&my>=y&&my<=y+BH) self.$emit('filtrar-clase',lbl); });
      };
      cv.removeEventListener('click',cv._ch); cv._ch=ch; cv.addEventListener('click',ch); cv.style.cursor='pointer';
      this.charts[key]={destroy:()=>{ ctx.clearRect(0,0,cv.width,cv.height); cv.removeEventListener('click',cv._ch); }};
    },

    barV(key, obj) {
      const cv=this.canvases[key]; if(!cv)return;
      this.charts[key]?.destroy?.();
      const labels=Object.keys(obj),vals=Object.values(obj); if(!labels.length)return;
      const PL=38,PR=16,PT=16,PB=36;
      const ancho=cv.offsetWidth||cv.parentElement?.offsetWidth||cv.parentElement?.parentElement?.offsetWidth||460;
      cv.width=ancho; cv.height=200;
      const ctx=cv.getContext('2d'),W=cv.width,H=cv.height,dH=H-PT-PB,dW=W-PL-PR,maxV=Math.max(...vals)||1;
      ctx.clearRect(0,0,W,H);
      const steps=4;
      for(let i=0;i<=steps;i++){
        const y=PT+dH-(dH/steps)*i; ctx.strokeStyle='#e8eaed'; ctx.lineWidth=1;
        ctx.beginPath(); ctx.moveTo(PL,y); ctx.lineTo(W-PR,y); ctx.stroke();
        ctx.fillStyle='#9ca3af'; ctx.font='11px system-ui'; ctx.textAlign='right'; ctx.fillText(Math.round((maxV/steps)*i),PL-5,y+4);
      }
      const palette=[this.C.verde,this.C.naranja,this.C.rojo,'#7c3aed'];
      const slotW=dW/labels.length,bW=Math.min(80,slotW*.55);
      labels.forEach((lbl,i)=>{
        const x=PL+slotW*i+(slotW-bW)/2,bH=(vals[i]/maxV)*dH,y=PT+dH-bH,r=4;
        ctx.fillStyle=palette[i%palette.length];
        ctx.beginPath(); ctx.moveTo(x+r,y); ctx.lineTo(x+bW-r,y); ctx.quadraticCurveTo(x+bW,y,x+bW,y+r);
        ctx.lineTo(x+bW,y+bH); ctx.lineTo(x,y+bH); ctx.lineTo(x,y+r); ctx.quadraticCurveTo(x,y,x+r,y);
        ctx.closePath(); ctx.fill();
        ctx.fillStyle='#374151'; ctx.font='12px system-ui'; ctx.textAlign='center'; ctx.fillText(lbl,x+bW/2,H-8);
        if(bH>16){ ctx.fillStyle='white'; ctx.font='bold 11px system-ui'; ctx.fillText(vals[i],x+bW/2,y+bH/2+4); }
      });
      this.charts[key]={destroy:()=>ctx.clearRect(0,0,W,H)};
    },
    _cookieParams() {
      const cookie = {};
      document.cookie.split(';').forEach(el => {
          const [k, v] = el.split('=');
          cookie[k.trim()] = decodeURIComponent(v ?? '');
      });
      return { email: cookie['Email'], id_rol: cookie['IdRol'] };
    },

    abrirModalSoat() {
        this.modalSoat.visible = true;
        if (this.modalSoat.rows.length) return; // ya cargado
        this.modalSoat.loading = true;
        window.api.call('post', 'dashboard_modal_soat', this._cookieParams())
            .then(res => { this.modalSoat.rows = res.data.data ?? []; })
            .catch(() => { this.modalSoat.rows = []; })
            .finally(() => { this.modalSoat.loading = false; });
    },

    abrirModalRtm() {
        this.modalRtm.visible = true;
        if (this.modalRtm.rows.length) return;
        this.modalRtm.loading = true;
        window.api.call('post', 'dashboard_modal_rtm', this._cookieParams())
            .then(res => { this.modalRtm.rows = res.data.data ?? []; })
            .catch(() => { this.modalRtm.rows = []; })
            .finally(() => { this.modalRtm.loading = false; });
    },

    abrirModalLicencias() {
        this.modalLicencias.visible = true;
        if (this.modalLicencias.rows.length) return;
        this.modalLicencias.loading = true;
        window.api.call('post', 'dashboard_modal_licencias', this._cookieParams())
            .then(res => { this.modalLicencias.rows = res.data.data ?? []; })
            .catch(() => { this.modalLicencias.rows = []; })
            .finally(() => { this.modalLicencias.loading = false; });
    },

    abrirModalAntecedentes() {
        this.modalAntecedentes.visible = true;
        if (this.modalAntecedentes.rows.length) return;
        this.modalAntecedentes.loading = true;
        window.api.call('post', 'dashboard_modal_antecedentes', this._cookieParams())
            .then(res => { this.modalAntecedentes.rows = res.data.data ?? []; })
            .catch(() => { this.modalAntecedentes.rows = []; })
            .finally(() => { this.modalAntecedentes.loading = false; });
    },

    estadoBadgeClass(estado) {
        return {
            vigente:          'op-badge-estado--verde',
            activa:           'op-badge-estado--verde',
            sin_alerta:       'op-badge-estado--verde',
            por_vencer:       'op-badge-estado--naranja',
            exento:           'op-badge-estado--gris',
            vencido:          'op-badge-estado--rojo',
            vencida:          'op-badge-estado--rojo',
            retenida:         'op-badge-estado--naranja',
            con_comparendos:  'op-badge-estado--naranja',
            con_antecedentes: 'op-badge-estado--rojo',
        }[estado] || 'op-badge-estado--gris';
    },

    estadoLabel(estado) {
        return {
            vigente:          'Vigente',
            activa:           'Activa',
            sin_alerta:       'Sin alertas',
            por_vencer:       'Por vencer',
            exento:           'Exento',
            vencido:          'Vencido',
            vencida:          'Vencida',
            retenida:         'Retenida',
            con_comparendos:  'Con comparendos',
            con_antecedentes: 'Con antecedentes',
        }[estado] || estado;
    },

    fmtFecha(f) {
        if (!f) return '—';
        const [y, m, d] = f.split('-');
        return `${d}/${m}/${y}`;
    },

    fmtPesos(v) {
        if (!v) return '$0';
        return '$' + new Intl.NumberFormat('es-CO').format(v);
    },
    abrirModalClase() {
        this.modalClase.visible = true;
        if (this.modalClase.rows.length) return;
        this.modalClase.loading = true;
        window.api.call('post', 'dashboard_modal_clase', this._cookieParams())
            .then(res => { this.modalClase.rows = res.data.data ?? []; })
            .catch(() => { this.modalClase.rows = []; })
            .finally(() => { this.modalClase.loading = false; });
    },

    abrirModalComparendos() {
        this.modalComparendos.visible = true;
        if (this.modalComparendos.rows.length) return;
        this.modalComparendos.loading = true;
        window.api.call('post', 'dashboard_modal_comparendos', this._cookieParams())
            .then(res => { this.modalComparendos.rows = res.data.data ?? []; })
            .catch(() => { this.modalComparendos.rows = []; })
            .finally(() => { this.modalComparendos.loading = false; });
    },

    abrirModalAlertas() {
        this.modalAlertas.visible = true;
        this.modalAlertas.filtroColor = '';
    },

    clasesFiltradas() {
        const rows = this.modalClase.rows;
        if (!this.modalClase.filtroClase) return rows;
        return rows.filter(r => r.clase === this.modalClase.filtroClase);
    },

    clasesUnicas() {
        return [...new Set(this.modalClase.rows.map(r => r.clase))].sort();
    },

    comparendosFiltrados() {
        const rows = this.modalComparendos.rows;
        if (!this.modalComparendos.filtroRango) return rows;
        return rows.filter(r => r.rango === this.modalComparendos.filtroRango);
    },

    alertasFiltradas() {
        if (!this.modalAlertas.filtroColor) return this.alertasDetalle;
        return this.alertasDetalle.filter(a => a.color === this.modalAlertas.filtroColor);
    },

    rangoClass(rango) {
        return {
            'Sin comparendos': 'op-badge-estado--verde',
            '1–3':             'op-badge-estado--naranja',
            '4–10':            'op-badge-estado--rojo',
            '+10':             'op-badge-estado--rojo',
        }[rango] || 'op-badge-estado--gris';
    },
  },
};
</script>

<style scoped lang="scss">
$bg:#ffffff; $white:#fff; $navy:#0A354E; $g200:#e8eaed; $g500:#6b7280; $g700:#374151;
$verde:#22c55e; $naranja:#f97316; $rojo:#ef4444; $r:16px;

.op-dashboard { padding:1.25rem .5rem; background:$bg; min-height:400px; }

/* Topbar */
.op-topbar { display:flex; justify-content:space-between; align-items:center; margin-bottom:1rem; flex-wrap:wrap; gap:.5rem; }
.op-topbar-left { display:flex; align-items:center; gap:.75rem; }
.op-topbar-right { display:flex; align-items:center; gap:.75rem; }
.op-last-update { font-size:.78rem; color:$g500; display:flex; align-items:center; gap:.35rem; i{font-size:.75rem;} }
.op-refresh-btn {
  display:flex; align-items:center; gap:.4rem; background:$white; border:1.5px solid $g200;
  border-radius:8px; padding:.45rem 1rem; font-size:.82rem; font-weight:600; color:$g700;
  cursor:pointer; transition:all .2s;
  &:hover { border-color:$navy; color:$navy; }
  i { transition:transform .3s; }
  &--loading i { animation:spin .8s linear infinite; }
}
@keyframes spin { to { transform:rotate(360deg); } }

/* Toggle auto-refresh */
.op-autorefresh-toggle { display:flex; align-items:center; gap:.5rem; cursor:pointer; font-size:.78rem; color:$g500; }
.op-toggle-label {}
.op-autorefresh-toggle input { display:none; }
.op-toggle-slider {
  width:36px; height:20px; background:$g200; border-radius:10px; position:relative; transition:background .2s;
  &:after { content:''; position:absolute; width:16px; height:16px; background:white; border-radius:50%; top:2px; left:2px; transition:left .2s; box-shadow:0 1px 3px rgba(0,0,0,.2); }
}
.op-autorefresh-toggle input:checked + .op-toggle-slider { background:$navy; }
.op-autorefresh-toggle input:checked + .op-toggle-slider:after { left:18px; }

/* Section label */
.op-section-label { font-size:.68rem; font-weight:700; letter-spacing:.8px; text-transform:uppercase; color:$g500; margin:1.25rem 0 .6rem; }

/* KPI */
.op-kpi-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:1rem; margin-bottom:1rem; }
.op-kpi-card { background:$white; border-radius:$r; padding:1.25rem 1.5rem 1rem; border:1px solid $g200; transition:box-shadow .2s; }
.op-kpi-card:hover { box-shadow:0 4px 16px rgba(0,0,0,.07); }
.op-kpi-card--alert { border-top:3px solid $rojo; }
.op-kpi-card--warn  { border-top:3px solid $naranja; }
.op-kpi-top { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:.75rem; }
.op-kpi-icon { width:36px; height:36px; border-radius:10px; display:flex; align-items:center; justify-content:center; }
.op-kpi-icon i { font-size:1rem; }
.op-icon-blue   {background:#eff6ff;} .op-icon-blue i  {color:#3b82f6;}
.op-icon-green  {background:#f0fdf4;} .op-icon-green i {color:$verde;}
.op-icon-red    {background:#fef2f2;} .op-icon-red i   {color:$rojo;}
.op-icon-orange {background:#fff7ed;} .op-icon-orange i{color:$naranja;}
.op-kpi-badge { font-size:.72rem; font-weight:700; padding:.2rem .55rem; border-radius:20px; display:flex; align-items:center; gap:.2rem; }
.op-kpi-badge i { font-size:.6rem; }
.op-badge-up      {background:#f0fdf4; color:#16a34a;}
.op-badge-down    {background:#fef2f2; color:$rojo;}
.op-badge-neutral {background:#fef2f2; color:$rojo;}
.op-kpi-label { font-size:.67rem; font-weight:700; letter-spacing:.8px; color:$g500; text-transform:uppercase; margin-bottom:.25rem; }
.op-kpi-value { font-size:2.4rem; font-weight:800; color:$g700; line-height:1; margin-bottom:.35rem; font-variant-numeric:tabular-nums; }
.op-value-red    {color:$rojo;}
.op-value-orange {color:$naranja;}
.op-kpi-sub { font-size:.78rem; color:$g500; }

/* Charts */
.op-charts-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:1rem; margin-bottom:1rem; }
.op-charts-grid-single { display:grid; grid-template-columns:1fr; gap:1rem; margin-bottom:1rem; }
.op-chart-card { background:$white; border-radius:$r; padding:1.25rem 1.5rem; border:1px solid $g200; transition:box-shadow .2s; }
.op-chart-card:hover { box-shadow:0 4px 16px rgba(0,0,0,.07); }
.op-chart-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.25rem; }
.op-chart-title { font-size:1rem; font-weight:700; color:$g700; }
.op-chart-sub   { font-size:.78rem; color:$g500; margin-top:2px; }
.op-link-btn { background:none; border:none; cursor:pointer; font-size:.82rem; font-weight:600; color:$navy; display:flex; align-items:center; gap:.3rem; white-space:nowrap; padding:0; }
.op-link-btn i { font-size:.75rem; transition:transform .15s; }
.op-link-btn:hover i { transform:translateX(3px); }
.op-chart-body { display:flex; align-items:center; gap:2rem; }
.op-donut-wrap { position:relative; flex-shrink:0; width:180px; height:180px; }
.op-donut-wrap canvas { width:180px !important; height:180px !important; }
.op-donut-center { position:absolute; inset:0; display:flex; flex-direction:column; align-items:center; justify-content:center; pointer-events:none; }
.op-donut-pct { font-size:1.75rem; font-weight:800; color:$g700; line-height:1; font-variant-numeric:tabular-nums; }
.op-donut-lbl { font-size:.6rem; font-weight:700; letter-spacing:1px; color:$g500; text-transform:uppercase; margin-top:2px; }
.op-legend { flex:1; display:flex; flex-direction:column; gap:.75rem; }
.op-legend-item { display:grid; grid-template-columns:12px 1fr auto auto; align-items:center; gap:.5rem; font-size:.85rem; }
.op-legend-dot   { width:12px; height:12px; border-radius:3px; flex-shrink:0; }
.op-legend-label { color:$g700; font-weight:500; }
.op-legend-count { font-weight:700; color:$g700; font-variant-numeric:tabular-nums; min-width:28px; text-align:right; }
.op-legend-pct   { font-size:.75rem; color:$g500; min-width:30px; text-align:right; }
.op-bar-body { width:100%; min-height:80px; }
.op-bar-body canvas { display:block; width:100% !important; }

/* Alertas */
.op-alertas-card { background:$white; border-radius:$r; border:1px solid $g200; margin-top:1rem; overflow:hidden; }
.op-alertas-header { display:flex; align-items:flex-start; justify-content:space-between; padding:1.25rem 1.5rem; border-bottom:1px solid $g200; }
.op-alertas-title { font-size:1rem; font-weight:700; color:$g700; }
.op-alertas-sub   { font-size:.78rem; color:$g500; margin-top:2px; }
.op-alertas-thead {
  display:grid; grid-template-columns:160px 1fr 180px 130px 80px; gap:1rem;
  padding:.6rem 1.5rem; background:#f8f9fa; border-bottom:1px solid $g200;
  font-size:.68rem; font-weight:700; letter-spacing:.7px; color:$g500; text-transform:uppercase;
}
.op-alertas-row {
  display:grid; grid-template-columns:160px 1fr 180px 130px 80px; gap:1rem;
  padding:.9rem 1.5rem; align-items:center; border-bottom:1px solid #f1f3f4; transition:background .15s;
  &:last-child { border-bottom:none; }
  &:hover { background:#fafbfc; }
}
.op-alerta-badge { display:inline-flex; align-items:center; gap:.35rem; font-size:.72rem; font-weight:600; padding:.25rem .7rem; border-radius:20px; }
.op-alerta-badge i { font-size:.65rem; }
.op-alerta-badge--rojo    {background:#fef2f2; color:#dc2626;}
.op-alerta-badge--naranja {background:#fff7ed; color:#ea580c;}
.op-alerta-badge--azul    {background:#eff6ff; color:#2563eb;}
.op-alerta-badge--gris    {background:#f1f5f9; color:#64748b;}
.op-alerta-recurso { display:flex; align-items:center; gap:.75rem; }
.op-alerta-recurso-icon { width:32px; height:32px; border-radius:8px; background:$g200; display:flex; align-items:center; justify-content:center; flex-shrink:0; i{font-size:.85rem; color:$g500;} }
.op-alerta-recurso-name { font-size:.88rem; font-weight:600; color:$g700; }
.op-alerta-recurso-sub  { font-size:.75rem; color:$g500; margin-top:1px; }
.op-alerta-detalle { font-size:.83rem; color:$g700; }
.op-alerta-estado { font-size:.82rem; font-weight:600; font-family:monospace; }
.op-alerta-estado--rojo    {color:#dc2626;}
.op-alerta-estado--naranja {color:#ea580c;}
.op-alerta-ver-btn { background:none; border:1px solid $g200; border-radius:6px; padding:.3rem .7rem; font-size:.8rem; font-weight:600; color:$g700; cursor:pointer; display:inline-flex; align-items:center; gap:.3rem; transition:all .15s; i{font-size:.7rem;} &:hover{border-color:$navy; color:$navy;} }
.op-alertas-empty { padding:2.5rem; text-align:center; color:$g500; font-size:.875rem; i{margin-right:.5rem; color:$verde;} }

/* Modal */
.op-modal {}
.op-btn-navy { background:$navy !important; color:white !important; border:none !important; border-radius:8px !important; }
.op-btn-navy:hover { background:#0c4f77 !important; }
.op-modal-body { padding:.25rem 0; }
.op-modal-intro { font-size:.875rem; color:$g500; margin-bottom:1.25rem; }
.op-modal-grid { display:grid; grid-template-columns:140px 1fr; gap:.6rem 1rem; font-size:.875rem; }
.op-modal-label { color:$g500; font-weight:500; }
.op-modal-val { color:$g700; font-weight:500; }
.op-modal-estado--rojo    { color:#dc2626; font-weight:600; }
.op-modal-estado--naranja { color:#ea580c; font-weight:600; }
.op-modal-estado--verde   { color:#16a34a; font-weight:600; }

/* Skeleton */
@keyframes shimmer { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
.op-skeleton-grid   { display:grid; grid-template-columns:repeat(4,1fr); gap:1rem; margin-bottom:1.25rem; }
.op-skeleton-charts { display:grid; grid-template-columns:repeat(2,1fr); gap:1rem; }
.op-skeleton-kpi, .op-skeleton-chart {
  background:linear-gradient(90deg,#ececec 25%,#f5f5f5 50%,#ececec 75%);
  background-size:800px 100%; animation:shimmer 1.4s infinite linear; border-radius:12px;
}
.op-skeleton-kpi   { height:130px; }
.op-skeleton-chart { height:260px; }

/* Responsive */
@media(max-width:1100px){ .op-kpi-grid{grid-template-columns:repeat(2,1fr);} }
@media(max-width:768px){
  .op-kpi-grid{grid-template-columns:repeat(2,1fr);}
  .op-charts-grid{grid-template-columns:1fr;}
  .op-chart-body{flex-direction:column; align-items:flex-start; gap:1rem;}
  .op-donut-wrap{width:150px;height:150px;} .op-donut-wrap canvas{width:150px !important;height:150px !important;}
  .op-kpi-value{font-size:1.8rem;}
  .op-alertas-thead{display:none;}
  .op-alertas-row{grid-template-columns:1fr; gap:.5rem; padding:1rem 1.25rem;}
}
@media(max-width:480px){ .op-kpi-grid{grid-template-columns:1fr;} }
/* ══ Modales de tabla ══ */
.op-modal-tabla {
  :deep(.p-dialog-content) { padding: 0; }
  :deep(.p-dialog-header)  { border-bottom: 1px solid $g200; font-weight: 700; }
  :deep(.p-dialog-footer)  { border-top: 1px solid $g200; }
}

.op-modal-loading {
  padding: 3rem; text-align: center; color: $g500; font-size: .9rem;
  i { margin-right: .5rem; font-size: 1.1rem; }
}

.op-modal-resumen {
  display: flex; align-items: center; gap: .75rem; flex-wrap: wrap;
  padding: .875rem 1.5rem; background: #f8f9fa; border-bottom: 1px solid $g200;
}
.op-resumen-badge {
  font-size: .75rem; font-weight: 700; padding: .3rem .8rem;
  border-radius: 20px; display: inline-flex; align-items: center; gap: .3rem;
  &--verde   { background: #f0fdf4; color: #16a34a; }
  &--naranja { background: #fff7ed; color: #ea580c; }
  &--rojo    { background: #fef2f2; color: #dc2626; }
  &--gris    { background: #f1f5f9; color: #64748b; }
}
.op-resumen-total {
  margin-left: auto; font-size: .8rem; font-weight: 700; color: $g500;
}

.op-tabla-wrap {
  width: 100%; overflow-x: auto; max-height: 60vh; overflow-y: auto;
}
.op-tabla {
  width: 100%; border-collapse: collapse; font-size: .85rem;

  thead tr {
    position: sticky; top: 0; background: $white; z-index: 1;
  }
  th {
    text-align: left; padding: .75rem 1.5rem;
    font-size: .68rem; font-weight: 700; letter-spacing: .7px;
    text-transform: uppercase; color: $g500;
    border-bottom: 2px solid $g200; white-space: nowrap;
  }
  td {
    padding: .75rem 1.5rem; border-bottom: 1px solid #f1f3f4;
    color: $g700; vertical-align: middle;
  }
  tbody tr:last-child td { border-bottom: none; }
  tbody tr:hover td      { background: #fafbfc; }
}

.op-badge-estado {
  display: inline-block; font-size: .72rem; font-weight: 700;
  padding: .2rem .65rem; border-radius: 20px; white-space: nowrap;
  &--verde   { background: #f0fdf4; color: #16a34a; }
  &--naranja { background: #fff7ed; color: #ea580c; }
  &--rojo    { background: #fef2f2; color: #dc2626; }
  &--gris    { background: #f1f5f9; color: #64748b; }
}
/* Chips de filtro */
.op-chip-filtro {
  display: inline-flex; align-items: center; gap: .4rem;
  background: $white; border: 1.5px solid $g200; border-radius: 20px;
  padding: .3rem .85rem; font-size: .78rem; font-weight: 600; color: $g700;
  cursor: pointer; transition: all .15s;

  &:hover { border-color: $navy; color: $navy; }

  &--activo {
    background: $navy; border-color: $navy; color: $white;
    .op-chip-count { background: rgba(255,255,255,.25); color: $white; }
  }

  &--rojo   { &.op-chip-filtro--activo { background: #dc2626; border-color: #dc2626; } }
  &--naranja { &.op-chip-filtro--activo { background: #ea580c; border-color: #ea580c; } }
  &--azul   { &.op-chip-filtro--activo { background: #2563eb; border-color: #2563eb; } }
}

.op-chip-count {
  background: $g200; color: $g500; border-radius: 10px;
  padding: .05rem .45rem; font-size: .7rem; font-weight: 700;
  transition: all .15s;
}

/* Badge clase vehículo */
.op-badge-clase {
  display: inline-block; background: #eff6ff; color: #1d4ed8;
  font-size: .72rem; font-weight: 700; padding: .2rem .65rem; border-radius: 20px;
}

/* Número de comparendos destacado */
.op-num-comp {
  display: inline-block; font-size: .9rem; font-weight: 800;
  font-variant-numeric: tabular-nums; color: $g700;
  &--alerta { color: #dc2626; }
}

/* Celda vacía */
.op-tabla-empty {
  text-align: center; padding: 2.5rem !important;
  color: $g500; font-size: .875rem;
}
</style>